import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { useAuth } from "../../lib/auth";
import { ArrowRight, Clock, Trophy, ListChecks, GraduationCap } from "@phosphor-icons/react";

export default function StudentDashboard() {
  const { user } = useAuth();
  const [exams, setExams] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/exams"), api.get("/attempts")])
      .then(([e, a]) => {
        setExams(e.data);
        setAttempts(a.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const submitted = attempts.filter((a) => a.status === "submitted");
  const inProgress = attempts.filter((a) => a.status === "in_progress");
  const avg = submitted.length
    ? (submitted.reduce((s, a) => s + (a.percentage || 0), 0) / submitted.length).toFixed(1)
    : "—";
  const passed = submitted.filter((a) => a.result?.passed).length;

  return (
    <div>
      <PageHeader
        overline={`Welcome, ${user?.name?.split(" ")[0] || "Student"}`}
        title="Your assessment center"
        subtitle="Continue where you left off, browse available exams, and review past results."
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 border border-zinc-200 mb-12" data-testid="student-stats">
        {[
          { label: "Available Exams", value: exams.length, icon: ListChecks },
          { label: "Attempted", value: submitted.length, icon: GraduationCap },
          { label: "Passed", value: passed, icon: Trophy },
          { label: "Avg. Score", value: avg === "—" ? "—" : `${avg}%`, icon: Clock },
        ].map((s, i) => (
          <div key={s.label} className={`p-6 ${i < 3 ? "border-r border-zinc-200" : ""}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">{s.label}</div>
            <div className="font-display text-3xl font-bold tabular">{loading ? "…" : s.value}</div>
          </div>
        ))}
      </div>

      {/* In-progress */}
      {inProgress.length > 0 && (
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Resume</div>
          <div className="border border-amber-500 bg-amber-50 p-5 flex items-center justify-between" data-testid="resume-attempt-card">
            <div>
              <div className="font-display text-lg font-bold">{inProgress[0].exam_title}</div>
              <div className="text-xs text-zinc-600">Attempt in progress — pick up where you left off.</div>
            </div>
            <Link
              to={`/app/exam/${inProgress[0].exam_id}/take?attempt=${inProgress[0].attempt_id}`}
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 text-sm hover:bg-zinc-800"
              data-testid="resume-attempt-btn"
            >
              Resume <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      )}

      {/* Available exams */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Available exams</div>
          <Link to="/app/exams" className="text-xs link-underline text-zinc-900 font-medium">View all</Link>
        </div>
        {loading ? (
          <div className="border border-zinc-200 p-6 text-sm text-zinc-500">Loading…</div>
        ) : exams.length === 0 ? (
          <div className="border border-zinc-200 p-6 text-sm text-zinc-500">No exams available yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-0 border border-zinc-200" data-testid="exam-list">
            {exams.slice(0, 4).map((ex, i) => (
              <ExamCard exam={ex} key={ex.exam_id} bordered={i % 2 === 0} bottomBorder={i < exams.slice(0, 4).length - 2} />
            ))}
          </div>
        )}
      </div>

      {/* Recent results */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Recent results</div>
        {submitted.length === 0 ? (
          <div className="border border-zinc-200 p-6 text-sm text-zinc-500">No completed attempts yet.</div>
        ) : (
          <div className="border border-zinc-200">
            {submitted.slice(0, 5).map((a, i) => (
              <Link
                key={a.attempt_id}
                to={`/app/results/${a.attempt_id}`}
                className={`flex items-center justify-between p-5 hover:bg-zinc-50 ${i < submitted.length - 1 ? "border-b border-zinc-200" : ""}`}
                data-testid={`result-row-${a.attempt_id}`}
              >
                <div>
                  <div className="font-medium">{a.exam_title}</div>
                  <div className="text-xs text-zinc-500 mt-1">{new Date(a.submitted_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Score</div>
                    <div className="font-mono-tab text-lg font-bold">{a.score} / {a.total_marks}</div>
                  </div>
                  <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold border px-2 py-1 ${a.result?.passed ? "border-green-600 text-green-700 bg-green-50" : "border-red-600 text-red-700 bg-red-50"}`}>
                    {a.result?.passed ? "Pass" : "Fail"}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ExamCard({ exam, bordered, bottomBorder }) {
  return (
    <Link
      to={`/app/exam/${exam.exam_id}`}
      className={`p-6 hover:bg-zinc-50 transition-colors ${bordered ? "md:border-r border-zinc-200" : ""} ${bottomBorder ? "border-b border-zinc-200" : ""}`}
      data-testid={`exam-card-${exam.exam_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="font-display text-lg font-bold leading-tight">{exam.title}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] border border-zinc-300 px-2 py-0.5">{exam.duration_minutes}m</div>
      </div>
      <div className="text-xs text-zinc-600 mb-4 line-clamp-2">{exam.description}</div>
      <div className="flex items-center justify-between text-xs">
        <div className="text-zinc-500">
          {exam.question_ids?.length || 0} questions · {exam.total_marks} marks
        </div>
        <span className="text-zinc-900 font-medium link-underline">Open →</span>
      </div>
    </Link>
  );
}
